import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { KioskShell } from "../components/KioskShell";
import { Button } from "../components/ui/button";
import { ArrowRight, Gauge, Fuel, Cog, Tag, CheckCircle2, CreditCard, Loader2 } from "lucide-react";
import { WakzyCheckPanel } from "../components/WakzyCheckPanel";
import { toast } from "sonner";

export default function VehicleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [idx, setIdx] = useState(0);
  const [reserving, setReserving] = useState(false);

  useEffect(() => {
    api.get(`/vehicles/${id}`).then((r) => setVehicle(r.data));
  }, [id]);

  const reserveWithDeposit = async () => {
    setReserving(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        vehicle_id: id,
        origin_url: window.location.origin,
        customer_email: localStorage.getItem("wakzy_kiosk_email") || "",
        customer_name: localStorage.getItem("wakzy_kiosk_name") || "",
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout");
        setReserving(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setReserving(false);
    }
  };

  if (!vehicle) return <KioskShell onBack={() => navigate("/kiosk")}><div className="p-12 text-slate-400">Loading…</div></KioskShell>;

  const images = vehicle.images?.length ? vehicle.images : ["https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1600"];

  return (
    <KioskShell onBack={() => navigate("/kiosk")}>
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid lg:grid-cols-2 gap-10">
          {/* Gallery */}
          <div>
            <div
              className="aspect-[16/10] rounded-md surface overflow-hidden bg-cover bg-center"
              style={{ backgroundImage: `url(${images[idx]})` }}
              data-testid="vehicle-detail-image"
            />
            {images.length > 1 && (
              <div className="flex gap-2 mt-3">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    className={`w-24 h-16 bg-cover bg-center rounded-sm border-2 ${i === idx ? "border-[#D4AF37]" : "border-white/10"}`}
                    style={{ backgroundImage: `url(${img})` }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="fade-up">
            <div className="flex items-center gap-3 mb-3">
              <span className="pill pill-available">{vehicle.status}</span>
              <span className="label-eyebrow">{vehicle.year} · {vehicle.color}</span>
            </div>
            <h1 className="font-heading text-4xl lg:text-5xl font-light mb-2 chrome-text inline-block">{vehicle.make}</h1>
            <h2 className="font-heading text-2xl text-slate-300 mb-4">{vehicle.model}</h2>
            <div className="mb-6">
              <span className="chrome-nameplate" data-testid="chrome-nameplate">
                <span className="dot" />
                {vehicle.body_type} · {vehicle.transmission}
                <span className="dot" />
              </span>
            </div>
            <div className="flex items-baseline gap-3 mb-8">
              <span className="font-display text-6xl gold-text">
                ${vehicle.price.toLocaleString()}
              </span>
              <span className="text-slate-400 text-sm">Sale price</span>
            </div>
            <p className="text-slate-300 leading-relaxed mb-8">{vehicle.description}</p>

            <div className="grid grid-cols-2 gap-3 mb-8">
              {[
                { icon: Gauge, label: "Mileage", value: `${vehicle.mileage?.toLocaleString()} mi` },
                { icon: Fuel, label: "Fuel", value: vehicle.fuel_type },
                { icon: Cog, label: "Transmission", value: vehicle.transmission },
                { icon: Tag, label: "VIN", value: vehicle.vin, mono: true },
              ].map((s, i) => (
                <div key={i} className="surface p-4 rounded-sm">
                  <s.icon className="w-4 h-4 text-[#D4AF37] mb-2" />
                  <div className="label-eyebrow mb-1">{s.label}</div>
                  <div className={`text-sm ${s.mono ? "font-mono text-xs" : ""}`}>{s.value}</div>
                </div>
              ))}
            </div>

            {vehicle.features?.length > 0 && (
              <div className="mb-10">
                <div className="gold-divider" />
                <div className="label-eyebrow mb-3 mt-4" style={{ color: "var(--wakzy-gold)" }}>
                  Highlight features
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {vehicle.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="gold-divider" />
              </div>
            )}

            {/* WakzyCheck — vehicle history report */}
            <WakzyCheckPanel vehicle={vehicle} />

            <div className="flex flex-wrap gap-3">
              <Button
                size="lg"
                onClick={() => navigate(`/kiosk/checkout/${vehicle.id}`)}
                className="btn-cta rounded-sm h-14 px-8 text-base"
                data-testid="start-purchase-btn"
              >
                Start Purchase <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                size="lg"
                onClick={reserveWithDeposit}
                disabled={reserving || vehicle.status !== "available"}
                className="btn-gold rounded-sm h-14 px-8 text-base"
                data-testid="reserve-deposit-btn"
              >
                {reserving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CreditCard className="w-5 h-5 mr-2" />}
                Reserve · $500 deposit
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate(`/kiosk/test-drive/${vehicle.id}`)}
                className="rounded-sm h-14 px-8 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 bg-transparent"
                data-testid="test-drive-btn"
              >
                Schedule Test Drive
              </Button>
            </div>
            {vehicle.status === "pending" && (
              <p className="text-xs text-slate-500 mt-3">Already reserved by another customer.</p>
            )}
          </div>
        </div>
      </section>
    </KioskShell>
  );
}
